import { Text } from "@mantine/core";
import { useAppSelector } from "app/hooks";
import { BSelect } from "components";
import { selectActiveInventoryItems } from "features/admin/inventory/Inventory.slice";
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface InventoryItemSelectProps {
  required?: boolean;
  onChange: (
    value:
      | {
          id: string;
          name: string;
        }
      | undefined
  ) => void;
  error?: ReactNode;
  value?: string; // could be undefined if form has no initial values
  disabled?: boolean;
}

export const InventoryItemSelect = (props: InventoryItemSelectProps) => {
  const { t } = useTranslation();
  const items = useAppSelector(selectActiveInventoryItems);
  const data = items.map((o) => ({ value: o.id, label: o.name, code: o.code }));

  const onSelectChange = (value?: string) => {
    if (value) {
      const item = items.find((i) => i.id === value);
      if (item) {
        props.onChange({
          id: value,
          name: item.name,
        });
      }
    }
  };

  return (
    <BSelect
      {...props}
      itemComponent={SelectItem}
      filter={(value, item) =>
        item.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item.code?.toLowerCase().includes(value.toLowerCase().trim())
      }
      data={data}
      onChange={onSelectChange}
      label={t("INVENTORY_ITEM")}
      nothingFound={t("NOTHING_FOUND")}
      limit={20}
    />
  );
};

interface ItemProps extends ComponentPropsWithoutRef<"div"> {
  label: string;
  code?: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ code, label, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <div>
        <Text>{label}</Text>

        {code && (
          <Text size="xs" color="dimmed">
            {code}
          </Text>
        )}
      </div>
    </div>
  )
);
